import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { Spinner } from 'react-bootstrap';
import '../../components/HostedCheckout/HostedCheckout.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import { rememberAnnualOfferPurchaseToken } from '../../utils/annualOffer';
import { buildPostPurchasePath } from '../../utils/postPurchaseConnectBroker';
import { ANALYTICS_EVENTS, capturePostHogEvent, pushEvent } from '../../utils/analytics';
import {
  buildHostedCheckoutNavigation,
  completeHostedCheckoutReturn,
  HostedCheckoutError,
  ownCheckoutPath,
} from '../../utils/hostedCheckout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
const MAX_PROCESSING_POLLS = 10;
const checkoutBridge = z.object({
  attemptId: z.string().min(1),
  returnToken: z.string().min(1),
  items: z
    .array(
      z.object({
        productSlug: z.enum(['starter-plan', 'light-plan']),
        planKey: z.string().nullable(),
        quantity: z.literal(1),
      }),
    )
    .length(1),
});

export default function HostedCheckoutReturnPage() {
  const { handleCustomJwtAuth } = useUnifiedAuth();
  return <HostedCheckoutReturn authenticate={handleCustomJwtAuth} />;
}

export function HostedCheckoutReturn({
  authenticate,
}: {
  authenticate: (token: string) => Promise<boolean>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const requestRef = useRef<{
    search: string;
    promise: ReturnType<typeof completeHostedCheckoutReturn>;
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    const params = new URLSearchParams(location.search);
    const attemptId = params.get('attempt');
    const returnToken = params.get('state');
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled') === 'true';
    if (!attemptId || !returnToken || (!canceled && !sessionId)) {
      setError('Checkout return link is incomplete.');
      return;
    }

    let active = true;
    let pollTimer: number | undefined;
    const finish = async () => {
      for (let poll = 0; poll < MAX_PROCESSING_POLLS && active; poll += 1) {
        if (requestRef.current?.search !== location.search) {
          requestRef.current = {
            search: location.search,
            promise: completeHostedCheckoutReturn({
              apiUrl: API_URL,
              attemptId,
              returnToken,
              sessionId,
              canceled,
            }),
          };
        }
        const request = requestRef.current;
        let result: Awaited<ReturnType<typeof completeHostedCheckoutReturn>>;
        try {
          result = await request.promise;
        } catch (requestError) {
          if (requestRef.current === request) requestRef.current = null;
          // The return endpoint is idempotent. A paid buyer must not be left on
          // an error page because one Stripe/reconciliation request returned a
          // transient 502; retry the same signed proof within the existing
          // bounded processing loop. Permanent proof, ownership, and payment
          // failures keep their explicit error and are never retried.
          if (
            requestError instanceof HostedCheckoutError &&
            requestError.code === 'HOSTED_CHECKOUT_UNAVAILABLE' &&
            poll < MAX_PROCESSING_POLLS - 1
          ) {
            await new Promise<void>((resolve) => {
              pollTimer = window.setTimeout(resolve, 1000);
            });
            continue;
          }
          throw requestError;
        }
        if (!active) return;
        if (requestRef.current === request) requestRef.current = null;
        if (result.status === 'canceled') {
          capturePostHogEvent('checkout_experience_cancelled', {
            checkout_attempt_id: attemptId,
          });
          // "Have a different promo code? Enter it here" on the Stripe page
          // comes back through this cancel return (Rob, 2026-09-29).
          // Stripe can open this in a new tab with no cart. Restore only the
          // server-verified attempt items; the return URL is editable. The
          // proof stays in transient router state, separate from the cart.
          const wantsPromoEntry = params.get('promo_entry') === 'true';
          const bridge = checkoutBridge.safeParse(result.checkoutBridge);
          const canRestorePromoCheckout =
            wantsPromoEntry &&
            bridge.success &&
            bridge.data.attemptId === attemptId &&
            bridge.data.returnToken === returnToken;
          if (canRestorePromoCheckout) {
            navigate(ownCheckoutPath(bridge.data.items), {
              replace: true,
              state: {
                hostedCheckoutBridge: {
                  attemptId: bridge.data.attemptId,
                  returnToken: bridge.data.returnToken,
                },
              },
            });
          } else {
            navigate(result.cancelPath || '/pricing?checkoutCanceled=true', { replace: true });
          }
          return;
        }
        if (result.status === 'processing') {
          await new Promise<void>((resolve) => {
            pollTimer = window.setTimeout(resolve, 1000);
          });
          continue;
        }
        rememberAnnualOfferPurchaseToken(result);
        if (result.token) {
          try {
            const authenticated = await authenticate(result.token);
            if (!active) return;
            if (!authenticated && !result.annualOfferToken)
              throw new Error('Payment verified. Please sign in to open your account.');
          } catch (authenticationError) {
            if (!result.annualOfferToken) throw authenticationError;
          }
        }
        // The server owns canonical Purchase and PaymentReceived events. A
        // verified return records completion, without duplicating revenue.
        if (result.purchase?.transactionId) {
          pushEvent({
            event: ANALYTICS_EVENTS.CHECKOUT_COMPLETED,
            transaction_id: result.purchase.transactionId,
            sessionId,
            currency: result.purchase.currency,
            plan: (result.purchase.productSlugs || []).join(','),
            revenue_event_owner: 'server',
            plan_type: (result.purchase.productSlugs || []).join(','),
            source: '/checkout/hosted-return',
            event_origin: 'hosted_checkout',
            checkout_attempt_id: attemptId,
          });
        }
        const destination = buildHostedCheckoutNavigation({
          redirectTo: result.redirectTo,
          returnPath: result.returnPath,
          userEmail: result.userEmail,
          origin: window.location.origin,
        });
        const productSlugs: string[] = result.purchase?.productSlugs || [];
        const planKeys: string[] = result.purchase?.planKeys || [];
        const monthlyProductSlugs = productSlugs.filter(
          (_slug, index) => planKeys[index] !== 'annual',
        );
        // Annual offer (monthly plans only), then the Connect Your Broker
        // step for every BotSpot plan buyer (Rob, 2026-10-12).
        navigate(buildPostPurchasePath(destination.path, productSlugs, monthlyProductSlugs), {
          replace: true,
          state: destination.state,
        });
        return;
      }
      if (active)
        throw new Error(
          'Payment is still processing. Check your email or account in a few minutes.',
        );
    };
    void finish().catch((returnError) => {
      if (active)
        setError(
          returnError instanceof Error ? returnError.message : 'Checkout could not be verified.',
        );
    });
    return () => {
      active = false;
      window.clearTimeout(pollTimer);
    };
  }, [authenticate, location.search, navigate]);

  return (
    <main className="checkout-page" aria-live="polite">
      <div className="checkout-loading hosted-checkout-status">
        {error ? (
          <>
            <h1>Payment needs review</h1>
            <p role="alert">{error}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/account-settings/subscriptions')}
            >
              View account
            </button>
          </>
        ) : (
          <>
            <Spinner animation="border" />
            <span>Verifying payment and opening your account…</span>
          </>
        )}
      </div>
    </main>
  );
}
